import { Button, Stack, Typography } from "@mui/material";
import { useExpiringRelatedPartiesQuery } from "Api/Queries/Party/useExpiringRelatedPartiesQuery";
import { Colors } from "Components/Layout/Themes/Colors";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useFormatLocale } from "Hooks/useFormatLocale";
import { isValid } from "date-fns";
import type { ExpiringRelatedPartyDocumentDto } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { getDocumentExpirationStatus } from "Utils/DocumentExpiration/documentExpirationUtils";
import { appUrl } from "Utils/UrlUtils";

const COMPONENT_RESOURCES = Resources.Components.RelatedPartyDocumentExpiration;
const RELATION_RESOURCES = Resources.Form.RelationType;

type ExpirationWarning = {
  key: string;
  isExpired: boolean;
  message: string;
};

const buildExpirationWarnings = (
  items: Array<ExpiringRelatedPartyDocumentDto>,
  t: TFunction,
  format: ReturnType<typeof useFormatLocale>,
): Array<ExpirationWarning> =>
  items.flatMap(
    ({
      name,
      relationType,
      identificationValidTo,
      secondIdentificationValidTo,
      partyPublicID,
    }) => {
      const relation = t(RELATION_RESOURCES[relationType]);

      return [
        { side: "primary", validTo: identificationValidTo },
        { side: "secondary", validTo: secondIdentificationValidTo },
      ].flatMap(({ validTo, side }): Array<ExpirationWarning> => {
        if (!validTo) {
          return [];
        }

        const { isExpired, isExpiringSoon } =
          getDocumentExpirationStatus(validTo);

        if (!isExpired && !isExpiringSoon) {
          return [];
        }

        const key = `${partyPublicID}-${side}`;

        if (isExpired) {
          return [
            {
              key,
              isExpired: true,
              message: t(COMPONENT_RESOURCES.ExpiredMessage, {
                name,
                relation,
              }),
            },
          ];
        }

        // Only the warning (not-yet-expired) message shows the expiry date.
        const date = new Date(validTo);
        const formattedDate = isValid(date) ? format(date) : "";

        return [
          {
            key,
            isExpired: false,
            message: t(COMPONENT_RESOURCES.WarningMessage, {
              name,
              relation,
              date: formattedDate,
            }),
          },
        ];
      });
    },
  );

export const RelatedPartyExpirationWarningPopup = () => {
  const { t } = useResource();
  const { data, isLoading } = useExpiringRelatedPartiesQuery();
  const format = useFormatLocale();
  const navigate = useNavigate();

  if (isLoading || !data) {
    return null;
  }

  const warnings = buildExpirationWarnings(data.items, t, format);

  if (warnings.length === 0) {
    return null;
  }

  const hasAnyExpired = warnings.some((warning) => warning.isExpired);

  return (
    <PrimaryPaper>
      <Stack gap={2}>
        <Typography align="left" fontWeight={600}>
          {t(COMPONENT_RESOURCES.Title)}
        </Typography>
        <Stack my={2} gap={1}>
          {warnings.map((warning) => (
            <Typography
              key={warning.key}
              align="left"
              color={warning.isExpired ? Colors.Red : Colors.WarningDark}
            >
              {warning.message}
            </Typography>
          ))}
        </Stack>
        <Button
          variant="contained"
          color={hasAnyExpired ? "error" : "warning"}
          onClick={() => {
            navigate(`${appUrl("user-account")}?activeTab=company`);
          }}
        >
          {t(COMPONENT_RESOURCES.UpdateDocuments)}
        </Button>
      </Stack>
    </PrimaryPaper>
  );
};
